import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiCreditCard, FiX, FiPlus } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const TABS = [
  { key: 'mappings', label: 'Account Mappings' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'payouts', label: 'Payouts' },
];

const emptyMappingForm = () => ({ provider_name: '', entity: '', clearing_account: '', receivable_account: '' });
const emptyReconcileForm = () => ({ provider_name: '', provider_payout_id: '', entity: '' });

const PaymentProviderConfig = () => {
  const [tab, setTab] = useState('mappings');
  const [entities, setEntities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [mappingForm, setMappingForm] = useState(emptyMappingForm());
  const [reconcileForm, setReconcileForm] = useState(emptyReconcileForm());
  const [saving, setSaving] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, accRes, mapRes, txRes, poRes] = await Promise.all([
        financeAPI.entities.list(), financeAPI.accounts.list(),
        financeAPI.paymentProviderMappings.list(), financeAPI.paymentTransactions.list(), financeAPI.paymentPayouts.list(),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setAccounts(accRes.data.results || accRes.data || []);
      setMappings(mapRes.data.results || mapRes.data || []);
      setTransactions(txRes.data.results || txRes.data || []);
      setPayouts(poRes.data.results || poRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load payment configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitMapping = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.paymentProviderMappings.create(mappingForm);
      setMappingForm(emptyMappingForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create mapping.');
    } finally {
      setSaving(false);
    }
  };

  const submitReconcile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setReconcileResult(null);
    try {
      const res = await financeAPI.paymentPayouts.reconcile(reconcileForm.provider_name, reconcileForm.provider_payout_id, reconcileForm.entity);
      setReconcileResult(res.data);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || 'Failed to reconcile payout.');
    } finally {
      setSaving(false);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || '—';
  const mappingAccounts = accounts.filter((a) => !mappingForm.entity || String(a.entity) === String(mappingForm.entity));

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiCreditCard size={22} />Payment Providers</h1>
          <p style={fs.pageSubtitle}>Every provider (Stripe, or a local rail) plugs in through the same interface — the ledger never talks to a provider directly.</p>
        </div>
        <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${tab === t.key ? fs.colors.purple : fs.colors.cardBorder}`,
            background: tab === t.key ? 'rgba(1, 31, 243,0.15)' : fs.colors.card,
            color: tab === t.key ? fs.colors.purple : '#888',
          }}>{t.label}</button>
        ))}
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {tab === 'mappings' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Mapping</button>
          </div>
          {showForm && (
            <form onSubmit={submitMapping} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
              <div><div style={fs.label}>Provider Name</div><input required placeholder="fake / stripe" value={mappingForm.provider_name} onChange={(e) => setMappingForm({ ...mappingForm, provider_name: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
              <div><div style={fs.label}>Entity</div>
                <select required value={mappingForm.entity} onChange={(e) => setMappingForm({ ...mappingForm, entity: e.target.value, clearing_account: '', receivable_account: '' })} style={{ ...fs.select, width: '100%' }}>
                  <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div><div style={fs.label}>Clearing Account</div>
                <select required value={mappingForm.clearing_account} onChange={(e) => setMappingForm({ ...mappingForm, clearing_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                  <option value="">Select…</option>{mappingAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </div>
              <div><div style={fs.label}>Receivable Account</div>
                <select required value={mappingForm.receivable_account} onChange={(e) => setMappingForm({ ...mappingForm, receivable_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                  <option value="">Select…</option>{mappingAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </div>
              <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create'}</button>
            </form>
          )}
          {loading ? <div style={fs.loadingState}>Loading…</div> : mappings.length === 0 ? (
            <div style={fs.emptyState}><FiCreditCard size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No provider mappings configured yet.</div></div>
          ) : (
            <div style={fs.tableWrap}>
              <table style={fs.table}>
                <thead><tr style={fs.tableHeadRow}>{['Provider', 'Entity', 'Clearing Account', 'Receivable Account', 'Active'].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {mappings.map((m, i) => (
                    <tr key={m.id} style={{ borderBottom: i < mappings.length - 1 ? '1px solid #111' : 'none' }}>
                      <td style={{ ...fs.td, color: fs.colors.textBright }}>{m.provider_name}</td>
                      <td style={fs.td}>{entityName(m.entity)}</td>
                      <td style={fs.td}>{accounts.find((a) => a.id === m.clearing_account)?.name || m.clearing_account}</td>
                      <td style={fs.td}>{accounts.find((a) => a.id === m.receivable_account)?.name || m.receivable_account}</td>
                      <td style={fs.td}>{m.is_active ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'transactions' && (
        loading ? <div style={fs.loadingState}>Loading…</div> : transactions.length === 0 ? (
          <div style={fs.emptyState}><FiCreditCard size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No payment transactions yet — these are created by provider webhooks.</div></div>
        ) : (
          <div style={fs.tableWrap}>
            <table style={fs.table}>
              <thead><tr style={fs.tableHeadRow}>{['Provider', 'Charge ID', 'Entity', 'Amount', 'Status', 'Journal Entry'].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
              <tbody>
                {transactions.map((t, i) => (
                  <tr key={t.id} style={{ borderBottom: i < transactions.length - 1 ? '1px solid #111' : 'none' }}>
                    <td style={{ ...fs.td, color: fs.colors.textBright }}>{t.provider_name}</td>
                    <td style={{ ...fs.td, fontFamily: 'monospace', fontSize: 11 }}>{t.provider_charge_id}</td>
                    <td style={fs.td}>{entityName(t.entity)}</td>
                    <td style={fs.td}>{fs.money(t.amount, t.currency)}</td>
                    <td style={fs.td}><span style={fs.badge(fs.STATUS_COLORS[t.status] || '#888')}>{t.status}</span></td>
                    <td style={fs.td}>{t.journal_entry ? `JE#${t.journal_entry} (draft — approve via Journal Entries)` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === 'payouts' && (
        <>
          <form onSubmit={submitReconcile} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
            <div><div style={fs.label}>Provider Name</div><input required placeholder="fake / stripe" value={reconcileForm.provider_name} onChange={(e) => setReconcileForm({ ...reconcileForm, provider_name: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
            <div><div style={fs.label}>Payout ID</div><input required value={reconcileForm.provider_payout_id} onChange={(e) => setReconcileForm({ ...reconcileForm, provider_payout_id: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
            <div><div style={fs.label}>Entity</div>
              <select required value={reconcileForm.entity} onChange={(e) => setReconcileForm({ ...reconcileForm, entity: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Reconciling…' : 'Reconcile Payout'}</button>
          </form>

          {reconcileResult && (
            <div style={{ ...fs.card, marginBottom: 22, border: `1px solid ${reconcileResult.reconciles_cleanly ? 'rgba(67,233,123,0.4)' : 'rgba(245,87,108,0.4)'}` }}>
              <div style={{ color: reconcileResult.reconciles_cleanly ? fs.colors.green : fs.colors.red, fontWeight: 700, marginBottom: 6 }}>
                {reconcileResult.reconciles_cleanly ? '✓ Reconciles cleanly' : '✗ Discrepancy found'}
              </div>
              <div style={{ fontSize: 12, color: fs.colors.textDim }}>
                Matched: {reconcileResult.matched_charge_ids.length} · Unmatched: {reconcileResult.unmatched_charge_ids.length}
                {reconcileResult.unmatched_charge_ids.length > 0 && ` (${reconcileResult.unmatched_charge_ids.join(', ')})`}
              </div>
            </div>
          )}

          {loading ? <div style={fs.loadingState}>Loading…</div> : payouts.length === 0 ? (
            <div style={fs.emptyState}><FiCreditCard size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No payouts reconciled yet.</div></div>
          ) : (
            <div style={fs.tableWrap}>
              <table style={fs.table}>
                <thead><tr style={fs.tableHeadRow}>{['Provider', 'Payout ID', 'Entity', 'Amount', 'Discrepancy', 'Clean'].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {payouts.map((p, i) => (
                    <tr key={p.id} style={{ borderBottom: i < payouts.length - 1 ? '1px solid #111' : 'none' }}>
                      <td style={{ ...fs.td, color: fs.colors.textBright }}>{p.provider_name}</td>
                      <td style={{ ...fs.td, fontFamily: 'monospace', fontSize: 11 }}>{p.provider_payout_id}</td>
                      <td style={fs.td}>{entityName(p.entity)}</td>
                      <td style={fs.td}>{fs.money(p.amount, p.currency)}</td>
                      <td style={fs.td}>{p.discrepancy}</td>
                      <td style={fs.td}>{p.reconciles_cleanly ? <span style={fs.badge(fs.colors.green)}>YES</span> : <span style={fs.badge(fs.colors.red)}>NO</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentProviderConfig;
